/* Global helpers: drawers, focus management, quantity inputs, carousels, accordions. */
(function () {
  'use strict';

  const theme = (window.theme = window.theme || {});

  theme.debounce = function (fn, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  theme.announce = function (message) {
    const region = document.querySelector('[data-live-region]');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 50);
  };

  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, iframe, [tabindex]:not([tabindex="-1"])';

  theme.getFocusable = function (container) {
    return Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    ).filter((el) => !el.closest('[inert]'));
  };

  /* ---------- Drawers (menu, filters, cart) ---------- */
  const Drawers = {
    current: null,
    trigger: null,

    open(id, trigger) {
      const drawer = document.getElementById(id);
      if (!drawer) return;
      if (this.current && this.current !== drawer) this.close({ restoreFocus: false });
      this.current = drawer;
      this.trigger = trigger || document.activeElement;
      drawer.classList.add('is-open');
      document.documentElement.classList.add('scroll-lock');
      const overlay = document.querySelector('[data-page-overlay]');
      if (overlay) {
        overlay.hidden = false;
        requestAnimationFrame(() => overlay.classList.add('is-visible'));
      }
      document.querySelectorAll(`[aria-controls="${id}"]`).forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
      drawer.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
      window.setTimeout(() => {
        const target = drawer.querySelector('[data-autofocus]') || drawer.querySelector('.drawer__close') || drawer;
        target.focus({ preventScroll: true });
      }, 60);
    },

    close(options = {}) {
      const drawer = this.current;
      if (!drawer) return;
      drawer.classList.remove('is-open');
      document.documentElement.classList.remove('scroll-lock');
      const overlay = document.querySelector('[data-page-overlay]');
      if (overlay) {
        overlay.classList.remove('is-visible');
        window.setTimeout(() => {
          if (!this.current) overlay.hidden = true;
        }, 250);
      }
      document.querySelectorAll(`[aria-controls="${drawer.id}"]`).forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
      drawer.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
      const trigger = this.trigger;
      this.current = null;
      this.trigger = null;
      if (options.restoreFocus !== false && trigger && document.contains(trigger)) {
        trigger.focus({ preventScroll: true });
      }
    },

    isOpen(id) {
      return this.current && this.current.id === id;
    }
  };
  theme.drawers = Drawers;

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      const id = opener.getAttribute('data-drawer-open');
      if (Drawers.isOpen(id)) Drawers.close();
      else Drawers.open(id, opener);
      return;
    }
    if (event.target.closest('[data-drawer-close]') || event.target.closest('[data-page-overlay]')) {
      if (Drawers.current) {
        event.preventDefault();
        Drawers.close();
      }
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!Drawers.current) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      Drawers.close();
      return;
    }
    if (event.key === 'Tab') {
      const focusable = theme.getFocusable(Drawers.current);
      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === Drawers.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  /* Close a drawer that is being re-rendered/removed in the theme editor. */
  document.addEventListener('shopify:section:unload', (event) => {
    if (Drawers.current && event.target.contains(Drawers.current)) Drawers.close({ restoreFocus: false });
  });

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.input = this.querySelector('input');
      this.addEventListener('click', this.onClick.bind(this));
      this.input.addEventListener('focus', () => this.input.select());
      this.updateButtons();
      this.input.addEventListener('change', () => this.updateButtons());
    }

    onClick(event) {
      const btn = event.target.closest('button');
      if (!btn || btn.disabled) return;
      event.preventDefault();
      const previous = this.input.value;
      if (btn.hasAttribute('data-quantity-plus')) this.input.stepUp();
      else if (btn.hasAttribute('data-quantity-minus')) this.input.stepDown();
      if (previous !== this.input.value) {
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    updateButtons() {
      const value = parseInt(this.input.value, 10) || 0;
      const min = parseInt(this.input.min, 10) || 0;
      const max = this.input.max ? parseInt(this.input.max, 10) : null;
      const minus = this.querySelector('[data-quantity-minus]');
      const plus = this.querySelector('[data-quantity-plus]');
      if (minus) minus.disabled = value <= min;
      if (plus) plus.disabled = max !== null && value >= max;
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* ---------- Carousel (native scroll-snap + optional arrows) ---------- */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');
      if (!this.track) return;
      this.update = this.update.bind(this);
      this.onPrev = () => this.scrollByPage(-1);
      this.onNext = () => this.scrollByPage(1);
      this.track.addEventListener('scroll', this.update, { passive: true });
      if (this.prev) this.prev.addEventListener('click', this.onPrev);
      if (this.next) this.next.addEventListener('click', this.onNext);
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.track);
      this.update();
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.update);
      if (this.prev) this.prev.removeEventListener('click', this.onPrev);
      if (this.next) this.next.removeEventListener('click', this.onNext);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    scrollByPage(direction) {
      const distance = this.track.clientWidth * 0.9 * direction;
      this.track.scrollBy({ left: document.dir === 'rtl' ? -distance : distance, behavior: 'smooth' });
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const left = Math.abs(this.track.scrollLeft);
      const scrollable = max > 4;
      this.classList.toggle('is-scrollable', scrollable);
      if (this.prev) this.prev.disabled = !scrollable || left <= 4;
      if (this.next) this.next.disabled = !scrollable || left >= max - 4;
    }
  }
  if (!customElements.get('slider-component')) customElements.define('slider-component', SliderComponent);

  /* ---------- Footer accordions: open on desktop, collapsible on mobile ---------- */
  const desktopQuery = window.matchMedia('(min-width: 750px)');
  class ResponsiveDetails extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.sync = () => {
        if (!this.details) return;
        if (desktopQuery.matches) this.details.setAttribute('open', '');
        else if (this.hasAttribute('data-open-mobile')) this.details.setAttribute('open', '');
        else this.details.removeAttribute('open');
      };
      this.onSummary = (event) => {
        if (desktopQuery.matches) event.preventDefault();
      };
      this.sync();
      desktopQuery.addEventListener('change', this.sync);
      const summary = this.querySelector('summary');
      if (summary) summary.addEventListener('click', this.onSummary);
    }

    disconnectedCallback() {
      desktopQuery.removeEventListener('change', this.sync);
    }
  }
  if (!customElements.get('responsive-details')) customElements.define('responsive-details', ResponsiveDetails);

  /* ---------- Localization selects submit on change ---------- */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-localization-select]');
    if (select && select.form) select.form.submit();
  });

  /* ---------- Read more (collection descriptions) ---------- */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-read-more]');
    if (!toggle) return;
    const target = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!target) return;
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!expanded));
    target.classList.toggle('is-expanded', !expanded);
  });

  /* ---------- Preselect saved values in address country selects ---------- */
  document.querySelectorAll('select[data-default]').forEach((select) => {
    const value = select.getAttribute('data-default');
    if (value && Array.from(select.options).some((o) => o.value === value)) select.value = value;
  });

  /* ---------- Section Rendering helper ---------- */
  theme.fetchSection = async function (url, sectionId, signal) {
    const separator = url.includes('?') ? '&' : '?';
    const response = await fetch(`${url}${separator}section_id=${encodeURIComponent(sectionId)}`, { signal });
    if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
    const text = await response.text();
    return new DOMParser().parseFromString(text, 'text/html');
  };

  /* ---------- Hero banners on phones: one at a time, change automatically, dots to show position ---------- */
  const phoneQuery = window.matchMedia('(max-width: 749px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  class HeroAutoplay extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.slides = [...this.querySelectorAll('.hero-banner')];
      if (this.slides.length < 2) return;
      this.delay = (parseInt(this.dataset.autoplay, 10) || 0) * 1000;
      this.index = 0;
      this.buildDots();
      this.onScroll = window.theme.debounce(() => this.syncIndex(), 80);
      this.addEventListener('scroll', this.onScroll, { passive: true });
      // Pause while the shopper touches or hovers the banners; resume afterwards.
      const pause = () => { this.paused = true; };
      const resume = () => { this.paused = false; this.restart(); };
      this.addEventListener('pointerdown', pause, { passive: true });
      this.addEventListener('pointerup', resume, { passive: true });
      this.addEventListener('pointercancel', resume, { passive: true });
      this.addEventListener('focusin', pause);
      this.addEventListener('focusout', resume);
      this.visible = true;
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; });
        this.io.observe(this);
      }
      this.onMedia = () => this.restart();
      phoneQuery.addEventListener('change', this.onMedia);
      this.restart();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      if (this.io) this.io.disconnect();
      if (this.onMedia) phoneQuery.removeEventListener('change', this.onMedia);
      if (this.dots) this.dots.remove();
      this._bound = false;
    }

    buildDots() {
      this.dots = document.createElement('div');
      this.dots.className = 'hero__dots';
      this.dots.setAttribute('aria-hidden', 'true');
      this.slides.forEach((_, i) => {
        const dot = document.createElement('span');
        dot.className = 'hero__dot' + (i === 0 ? ' is-active' : '');
        this.dots.appendChild(dot);
      });
      this.after(this.dots);
    }

    // The hero is position:relative on phones, so slide.offsetLeft is measured inside the scroll area.
    slideLeft(slide) {
      return slide.offsetLeft - (parseFloat(getComputedStyle(this).scrollPaddingLeft) || 0);
    }

    syncIndex() {
      const left = this.scrollLeft;
      let closest = 0;
      this.slides.forEach((slide, i) => {
        if (Math.abs(this.slideLeft(slide) - left) < Math.abs(this.slideLeft(this.slides[closest]) - left)) closest = i;
      });
      this.index = closest;
      [...this.dots.children].forEach((dot, i) => dot.classList.toggle('is-active', i === closest));
    }

    restart() {
      clearInterval(this.timer);
      if (!this.delay || !phoneQuery.matches || reducedMotion.matches) return;
      this.timer = setInterval(() => {
        if (this.paused || !this.visible || document.hidden) return;
        const next = (this.index + 1) % this.slides.length;
        const target = this.slideLeft(this.slides[next]);
        this.scrollTo({ left: target, behavior: next === 0 ? 'auto' : 'smooth' });
        // Fallback for browsers that skip smooth scrolling (e.g. power-saving mode): jump if it did not move.
        setTimeout(() => {
          if (Math.abs(this.scrollLeft - target) > 4) this.scrollTo({ left: target, behavior: 'auto' });
          this.syncIndex();
        }, 900);
      }, this.delay);
    }
  }
  if (!customElements.get('hero-autoplay')) customElements.define('hero-autoplay', HeroAutoplay);
})();